import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LucideScreenShare, Search, Trash2 } from "lucide-react";
import { useCompanies } from "@/hooks/queries/useCompanies";
import Loader from "@/components/Loader";
import { useDeleteCompany } from "@/hooks/mutations/companies";
import useAuth from "@/hooks/stores/useAuth";
import { useCompanyOfRecruiter } from "@/hooks/queries/recruiters";
import { LuScreenShare } from "react-icons/lu";
import Pagination from "@/components/Common/Pagination";
import { companiesService } from "@/services/companiesService";
import { recruitersService } from "@/services/recruitersService";
import { useDebounce } from "@/utils/helpers";

const Companies = () => {
  const { userRole } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 24,
    total: 1,
    last_page: 1,
    next_page_url: null,
    prev_page_url: null,
  });
  const isRecruiter = userRole.toLowerCase() === "recruiter";
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchData = async () => {
    const params = {
      per_page: pagination.per_page,
      page: pagination.current_page,
      search: searchTerm,
    };
    if (!isRecruiter) {
      setIsLoading(true);
      const response = await companiesService.getAllCompanies(params);
      setFilteredCompanies(response.data);
      setPagination(response.pagination);
      setIsLoading(false);
    } else {
      setIsLoading(true);
      const response = await recruitersService.getCompanyOfRecruiter(params);
      setFilteredCompanies(response.data);
      setPagination(response.pagination);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.current_page, pagination.per_page, searchTerm]);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [companyToDelete, setCompanyToDelete] = useState(null);
  const navigate = useNavigate();

  // function to handle search term input
  const updateSearchTerm = (term) => {
    setSearchTerm(term);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  // defined useDebounce for search term input
  const debouncedSearch = useDebounce(updateSearchTerm, 1000);

  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const handleSearchChange = (term) => {
    setTempSearchTerm(term);
    debouncedSearch(term);
  };

  const [currentCompanies, setCurrentCompanies] = useState([]);
  useEffect(() => {
    setCurrentCompanies(filteredCompanies);
  }, [filteredCompanies]);

  const { mutate: deleteCompany, isLoading: isDeleting } = useDeleteCompany();

  if (isLoading) {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-background">
        <Loader bgColor="transparent" loadingText="Loading companies..." />
      </div>
    );
  }

  if (isError) {
    return <div className="p-6 bg-background">Error loading companies.</div>;
  }

  const handleDeleteClick = (id) => {
    setCompanyToDelete(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    deleteCompany(companyToDelete, {
      onSuccess: () => {
        // Refresh the page so that the updated list is fetched
        window.location.reload();
      },
      onError: (error) => {
        console.error("Failed to delete company:", error.message);
      },
    });
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setCompanyToDelete(null);
  };

  const handlePageChange = (page) => {
    // Simply update the current page, fetchData will be triggered by the useEffect
    setPagination({ ...pagination, current_page: page });
  };
  return (
    <div className="flex flex-col min-h-screen bg-background">
      <div className="flex-1 w-[90%] pt-12  px-8 mx-auto flex flex-col gap-8">
        {/* Header Section */}
        <div className="flex flex-col gap-4">
          <div className="flex justify-between items-start flex-wrap gap-4">
            <div className="flex flex-col gap-1">
              <h1 className="text-foreground text-2xl font-semibold font-['Inter'] leading-8">
                Companies
              </h1>
              <p className="text-muted-foreground text-base font-normal font-['Inter'] leading-6">
                Manage companies, view account information, and track
                recruitment activity by business.
              </p>
            </div>
            {["admin", "company_admin"].includes(userRole.toLowerCase()) && (
              <button
                onClick={() => navigate("/companies/add")}
                className="bg-primary-dark h-[36px] text-white px-4  rounded-lg shadow-sm font-semibold transition-all hover:bg-primary-dark"
                id="add-company-btn"
              >
                Add Company
              </button>
            )}
          </div>
          <div className="w-full md:w-[384px]">
            <div className="self-stretch h-11 px-3.5 py-2.5 bg-background rounded-lg border border-input inline-flex justify-start items-center gap-2 overflow-hidden">
              <Search className="w-5 h-5 text-muted-foreground" />
              <input
                id="company-search-input"
                type="text"
                placeholder="Search"
                value={tempSearchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="flex-1 text-foreground placeholder:text-muted-foreground text-base font-normal leading-6 outline-none bg-transparent"
              />
            </div>
          </div>
        </div>

        {currentCompanies.length === 0 ? (
          <div className="flex justify-center items-center h-[calc(100vh-200px)]">
            <h3 className="text-lg font-medium text-text-foreground">
              No Companies Found
            </h3>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {currentCompanies.map((company) => (
              <div
                key={company.id}
                onClick={() => {
                  navigate(`/companies/view/${company.id}`, {
                    state: { activeTab: "overview" },
                  });
                }}
                className="flex flex-col bg-card rounded-xl shadow-[0px_1px_2px_0px_rgba(10,13,18,0.05)] outline outline-1 outline-border hover:shadow-md transition-all cursor-pointer relative overflow-hidden"
              >
                {/* Trash icon in top right */}
                {["admin", "company_admin"].includes(
                  userRole.toLowerCase(),
                ) && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(company.id);
                    }}
                    title="Delete Company"
                    className="absolute top-4 right-4 text-red-500 hover:text-red-700 transition-colors z-20 p-1 rounded-md hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}

                <div className="self-stretch p-5 flex flex-col justify-start items-start gap-6">
                  <div className="self-stretch inline-flex justify-start items-center gap-3">
                    {/* Logo/Icon */}
                    <div className="w-12 h-12 flex-shrink-0 flex items-center justify-center rounded-full border border-border overflow-hidden bg-card">
                      {company.logo?.url ? (
                        <img
                          src={company.logo.url}
                          alt={`${company.name} logo`}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <span className="text-xl font-bold text-foreground">
                          {company.name.charAt(0)}
                        </span>
                      )}
                    </div>
                    {/* Company Name */}
                    <div className="flex-1 inline-flex flex-col justify-start items-start overflow-hidden">
                      <div
                        className="self-stretch text-foreground text-base font-semibold font-['Inter'] leading-6 truncate"
                        title={company.name}
                      >
                        {company.name}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Footer / Actions */}
                <div className="mt-auto self-stretch pb-4 flex flex-col justify-start items-center gap-4">
                  <div className="self-stretch h-px bg-border" />
                  <div className="self-stretch px-6 inline-flex justify-end items-center gap-4">
                    <div className="flex-1 flex justify-end items-center gap-3">
                      {company.website ? (
                        <a
                          href={
                            company.website?.startsWith("http")
                              ? company.website
                              : `https://${company.website}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-foreground text-sm font-semibold font-['Inter'] leading-5 hover:underline transition-all"
                        >
                          View Website
                        </a>
                      ) : (
                        <span className="text-muted-foreground text-sm font-semibold font-['Inter'] leading-5">
                          No Website
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {showDeleteModal && (
          <div
            className="relative z-10"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
          >
            <div
              className="fixed inset-0 bg-foreground/40 transition-opacity"
              aria-hidden="true"
            ></div>
            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
              {isDeleting ? (
                <div className="flex min-h-full items-center justify-center p-4">
                  <Loader />
                </div>
              ) : (
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                  <div className="relative transform overflow-hidden rounded-lg bg-card text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                    <div className="bg-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                      <div className="sm:flex sm:items-start">
                        <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                          <svg
                            className="h-6 w-6 text-red-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            strokeWidth="1.5"
                            stroke="currentColor"
                            aria-hidden="true"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                            />
                          </svg>
                        </div>
                        <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                          <h3
                            className="text-base font-semibold text-foreground"
                            id="modal-title"
                          >
                            Delete Company
                          </h3>
                          <div className="mt-2">
                            <p className="text-sm text-muted-foreground">
                              Are you sure you want to delete this company? This
                              action cannot be undone.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-muted px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                      <button
                        type="button"
                        onClick={handleConfirmDelete}
                        className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 sm:ml-3 sm:w-auto"
                      >
                        Delete
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowDeleteModal(false)}
                        className="mt-3 inline-flex w-full justify-center rounded-md bg-card px-3 py-2 text-sm font-semibold text-foreground ring-1 shadow-xs ring-input ring-inset hover:bg-muted sm:mt-0 sm:w-auto"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <div className="p-6">
        <Pagination
          currentPage={pagination.current_page}
          totalPages={pagination.last_page}
          onPageChange={handlePageChange}
          per_page={pagination.per_page}
          className="w-[90%] mx-auto"
        />
      </div>
    </div>
  );
};

export default Companies;
